import React, { useState } from "react";
import styled from "@emotion/styled";
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from "@mui/icons-material/Error";
import { useSelector } from "react-redux";
import AddAssigneesInput from "./AddAssigneesInput";
import { selectAddAssigneesPerm } from "../../../../../slices/userActionsPermissionsSlice";
import { Button } from "../../../../../StyledComponents";

const AddAssignees = ({ teamMembersByName, handleCloseModal, activeLead, leadData, mortgageData, userId, userFullName }) => {
  const addAssigneesPerm = useSelector(selectAddAssigneesPerm)

  const [isError, setIsError] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  return (
    <AssigneesPanel>
      {isSuccess ?
        <PanelHeader $tone="success">
          <HeaderIcon>
            <CheckIcon />
          </HeaderIcon>
          <HeaderText>Success</HeaderText>
        </PanelHeader>
      : isError ?
        <PanelHeader $tone="error">
          <HeaderIcon>
            <ErrorIcon />
          </HeaderIcon>
          <HeaderText>
            Failed... please try again. Reach out to support@lancastersweep.com if the problem persists.
          </HeaderText>
        </PanelHeader>
      :
        <PanelHeader>
          <HeaderText>Manage Assignees</HeaderText>
          <HeaderMeta>Review investigation ownership and assign team members.</HeaderMeta>
        </PanelHeader>
      }

      <AssigneesBody>
        <BodyScroll>
          <InstructionCard>
            <InstructionTitle>Assignment updates</InstructionTitle>
            <InstructionCopy>
              Select one or more team members to assign. Team members assigned to the lead will receive all updates regarding the investigation.
            </InstructionCopy>
          </InstructionCard>
          <AddAssigneesInput entity={'lead'} currentAssigneeIds={leadData.assigneeIds} leadData={leadData} mortgageId={mortgageData._id} userFullName={userFullName} activeLead={activeLead} addAssigneesPerm={addAssigneesPerm} teamMembersByName={teamMembersByName} setIsError={setIsError} setIsSuccess={setIsSuccess} isSuccess={isSuccess} userId={userId} />
        </BodyScroll>
        <PanelFooter>
          <Button 
            className='neutral'
            onClick={() => handleCloseModal()}
          >
            Close
          </Button>
        </PanelFooter>
      </AssigneesBody>
    </AssigneesPanel>
  )
}

const AssigneesPanel = styled.section(({ theme }) => `
  width: 100%;
  height: 100%;
  max-height: 100%;
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  background: ${theme.palette.common.cardBackground};
  overflow: hidden;
`)

const PanelHeader = styled.header(({ theme, $tone }) => {
  const isSuccess = $tone === "success";
  const isError = $tone === "error";
  const toneColor = isSuccess
    ? theme.palette.primary.dark
    : isError
      ? theme.palette.common.redDark
      : theme.palette.common.grayText;
  const toneBackground = isSuccess
    ? theme.palette.primary.labelTransparent
    : isError
      ? theme.palette.common.redTransparent
      : theme.palette.common.white;

  return `
    min-height: 4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.7rem;
    padding: 0.8rem 1rem;
    border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
    background: ${toneBackground};
    color: ${toneColor};
  `;
})

const HeaderIcon = styled.div(({ theme }) => `
  width: 2.4rem;
  height: 2.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.6rem;
  background: ${theme.palette.common.white};
  border: 0.1rem solid currentColor;

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`)

const HeaderText = styled.span`
  color: inherit;
  font-size: 1.05rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
  font-family: -apple-system, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", 'Inter', "Helvetica Neue", sans-serif;
`

const HeaderMeta = styled.span(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 0.9rem;
  font-weight: 600;
`)

const AssigneesBody = styled.div(({ theme }) => `
  min-height: 0;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  background: ${theme.palette.common.grayBorderLight};
  overflow: hidden;
`)

const BodyScroll = styled.div(({ theme }) => `
  min-height: 0;
  height: 100%;
  max-height: 100%;
  display: grid;
  align-content: start;
  gap: 1rem;
  padding: 1rem;
  overflow-x: hidden;
  overflow-y: auto;
  background: ${theme.palette.common.cardBackground};

  ::-webkit-scrollbar-thumb {
    border: 0.1rem solid ${theme.palette.common.grayText};
    background-color: ${theme.palette.common.grayText};
  }

  ::-webkit-scrollbar {
    -webkit-appearance: none;
    border: 0.05rem solid ${theme.palette.common.grayBorderDark};
    background-color: ${theme.palette.common.pageBackground};
  }

  ::-webkit-scrollbar:vertical {
    width: 0.4rem;
  }
`)

const InstructionCard = styled.div(({ theme }) => `
  display: grid;
  gap: 0.4rem;
  padding: 1rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.8rem;
  background: ${theme.palette.common.white};
`)

const InstructionTitle = styled.div(({ theme }) => `
  color: ${theme.palette.common.black};
  font-size: 1rem;
  font-weight: 800;
`)

const InstructionCopy = styled.div(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.45;
`)

const PanelFooter = styled.footer(({ theme }) => `
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 1rem;
  border-top: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.white};
`)

export default AddAssignees;
